import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowRight, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useSite } from '../../hooks/useSiteConfig';

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
  </svg>
);

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, pinterest: PinterestIcon, linkedin: Linkedin, youtube: Youtube };

const DEFAULT_COLUMNS = [
  { id: 'products', title: 'Products', items: [
    { id: 'p1', label: 'Shades', url: '/products/shades' }, { id: 'p2', label: 'Blinds', url: '/products/blinds' },
    { id: 'p3', label: 'Drapery', url: '/products/drapery' }, { id: 'p4', label: 'Fabric Swatches', url: '/fabrics' },
    { id: 'p5', label: 'Residential', url: '/products/residential' }, { id: 'p6', label: 'Commercial Grade', url: '/products/commercial' },
    { id: 'p7', label: 'Architectural', url: '/products/architectural' }, { id: 'p8', label: 'Motorized Systems', url: '/motorization' }] },
  { id: 'company', title: 'Company', items: [
    { id: 'c1', label: 'About Us', url: '/about' }, { id: 'c2', label: 'Gallery', url: '/gallery' },
    { id: 'c4', label: 'Trade Program', url: '/trade' }, { id: 'c5', label: 'Blog', url: '/blog' }, { id: 'c6', label: 'FAQ', url: '/faq' }, { id: 'c7', label: 'Contact', url: '/contact' }] },
];
const DEFAULT_LEGAL = [{ id: 'l1', label: 'Privacy Policy', url: '/privacy' }, { id: 'l2', label: 'Terms of Service', url: '/terms' }];
const DEFAULT_GLOBALS = {
  brand_name: '',
  contact: {},
  social: {},
  global_cta: { text: 'Request Quote', link: '/quote' },
  footer: { description: 'Custom shades fabricated in Brooklyn, serving New York City and surrounding areas.', copyright: '© {year} {brand}. All rights reserved.' },
};

const FooterLink = ({ item, className }) => item.type === 'external'
  ? <a href={item.url} target="_blank" rel="noreferrer" className={className}>{item.label}</a>
  : <Link to={item.url || '#'} className={className}>{item.label}</Link>;

export const Footer = () => {
  const site = useSite();
  const g = { ...DEFAULT_GLOBALS, ...(site.globals || {}) };
  const columns = site.navigation?.footer?.length ? site.navigation.footer : DEFAULT_COLUMNS;
  const legal = site.navigation?.legal?.length ? site.navigation.legal : DEFAULT_LEGAL;
  const socials = Object.entries(g.social || {}).filter(([, url]) => url);
  const linkCls = 'text-neutral-400 hover:text-white transition-colors text-sm';

  return (
    <footer className="bg-[color:var(--site-dark)] text-white" data-testid="footer">
      <div className="container-xl section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center" data-testid="footer-logo-link">
              {g.logo_url ? (
                <img src={g.logo_url} alt={g.brand_name} data-testid="footer-logo-img"
                  className={g.logo_mono ? 'w-[140px] h-auto' : 'max-w-[160px] max-h-16 w-auto h-auto bg-white rounded-md p-2'}
                  style={g.logo_mono ? { filter: 'brightness(0) invert(1)' } : undefined} />
              ) : <span className="text-xl font-semibold" data-testid="footer-brand-name">{g.brand_name}</span>}
            </Link>
            <p className="mt-4 text-neutral-400 text-sm leading-relaxed">{g.footer?.description}</p>
            <div className="flex items-center gap-3 mt-6">
              {socials.map(([name, url]) => {
                const IconComponent = SOCIAL_ICONS[name];
                if (!IconComponent) return null;
                return (
                  <a key={name} href={url} target="_blank" rel="noopener noreferrer" aria-label={name} data-testid={`social-${name}`}
                    className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-[color:var(--site-accent)] rounded-full transition-colors">
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
            <div className="mt-6 space-y-3">
              {g.contact?.address && <div className="flex items-start gap-3 text-sm text-neutral-400"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{g.contact.address}</span></div>}
              {g.contact?.phone && <a href={`tel:${g.contact.phone.replace(/\D/g, '')}`} className="flex items-center gap-3 text-sm text-neutral-400 hover:text-white transition-colors"><Phone className="w-4 h-4 flex-shrink-0" /><span>{g.contact.phone}</span></a>}
              {g.contact?.email && <a href={`mailto:${g.contact.email}`} className="flex items-center gap-3 text-sm text-neutral-400 hover:text-white transition-colors"><Mail className="w-4 h-4 flex-shrink-0" /><span>{g.contact.email}</span></a>}
            </div>
          </div>

          {columns.slice(0, 2).map((col) => (
            <div key={col.id} data-testid={`footer-column-${col.id}`}>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">{col.title}</h4>
              <ul className="space-y-3">
                {(col.items || []).filter((i) => i.visible !== false).map((item) => <li key={item.id}><FooterLink item={item} className={linkCls} /></li>)}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Get Started</h4>
            <p className="text-neutral-400 text-sm mb-6">Ready to start your window treatment project? Get a custom quote today.</p>
            <Link to={g.global_cta?.link || '/quote'} data-testid="footer-cta"
              className="inline-flex items-center gap-2 bg-white text-neutral-900 px-6 py-3 text-sm font-medium uppercase tracking-wide hover:bg-neutral-100 transition-colors rounded-[var(--site-btn-radius)]">
              {g.global_cta?.text || 'Request Quote'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10" data-fa-avoid="" data-testid="footer-legal-bar">
        <div className="container-xl py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-neutral-500 text-sm">{(g.footer?.copyright || '').replace('{year}', new Date().getFullYear()).replace('{brand}', g.brand_name || '')}</p>
          <div className="flex items-center gap-6">
            {legal.filter((i) => i.visible !== false).map((item) => <FooterLink key={item.id} item={item} className="text-neutral-500 hover:text-white text-sm transition-colors" />)}
          </div>
        </div>
      </div>
    </footer>
  );
};
